import { useState } from 'preact/hooks';
import type { TextKey } from '../content/fr';
import { t } from '../engine/i18n';
import type { GameState } from '../engine/state';
import { Header } from './Header';
import { useGame } from './hooks';
import { Modals, Notices } from './Modals';
import { PrologueScreen } from './Prologue';
import { Side } from './Side';
import { BudgetTab } from './tabs/BudgetTab';
import { DashboardTab } from './tabs/DashboardTab';
import { HousingTab } from './tabs/HousingTab';
import { MarketTab } from './tabs/MarketTab';
import { PlacementsTab } from './tabs/PlacementsTab';
import { JournalTab } from './tabs/JournalTab';
import { NotebookTab } from './tabs/NotebookTab';
import { SavingsTab } from './tabs/SavingsTab';
import { WorkTab } from './tabs/WorkTab';

type TabId = 'dashboard' | 'work' | 'budget' | 'savings' | 'housing' | 'invest' | 'market' | 'notebook' | 'journal';

function availableTabs(state: GameState): { id: TabId; label: TextKey }[] {
  const hasExpenses = Object.keys(state.expenses).length > 0;
  const bourse = state.stage === 'stage2' || state.pea.open || state.av.open;
  const tabs: { id: TabId; label: TextKey; visible: boolean }[] = [
    { id: 'dashboard', label: 'tabs.dashboard', visible: true },
    { id: 'work', label: 'tabs.work', visible: true },
    { id: 'budget', label: 'tabs.budget', visible: true },
    { id: 'savings', label: 'tabs.savings', visible: hasExpenses },
    { id: 'housing', label: 'tabs.housing', visible: state.firstJobDay !== null && state.stage !== 'prologue' },
    // La bourse s'ouvre avec l'étape 2, une fois le fonds d'urgence complet (ou avec l'assurance-vie du conseiller) :
    // les placements d'un côté, le marché de l'autre.
    { id: 'invest', label: 'tabs.invest', visible: bourse },
    { id: 'market', label: 'tabs.market', visible: bourse },
    { id: 'notebook', label: 'tabs.notebook', visible: state.pages.length > 0 },
    { id: 'journal', label: 'tabs.journal', visible: true },
  ];
  return tabs.filter((tab) => tab.visible);
}

export function App() {
  const state = useGame();
  const [tab, setTab] = useState<TabId>('dashboard');
  const inPrologueScreens = state.stage === 'prologue' && state.prologueStep !== 'years';
  const tabs = availableTabs(state);
  const active = tabs.some((item) => item.id === tab) ? tab : 'dashboard';

  return (
    <div class="app">
      <Header state={state} />
      <div class="layout">
        <main class="main">
          {inPrologueScreens ? (
            <PrologueScreen state={state} />
          ) : (
            <>
              <nav class="tabs" role="tablist">
                {tabs.map((item) => (
                  <button
                    key={item.id}
                    role="tab"
                    aria-selected={item.id === active}
                    class={item.id === active ? 'tab active' : 'tab'}
                    onClick={() => setTab(item.id)}
                  >
                    {t(item.label)}
                  </button>
                ))}
              </nav>
              <section class="tab-panel" role="tabpanel">
                {active === 'dashboard' && <DashboardTab state={state} />}
                {active === 'work' && <WorkTab state={state} />}
                {active === 'budget' && <BudgetTab state={state} />}
                {active === 'savings' && <SavingsTab state={state} />}
                {active === 'housing' && <HousingTab state={state} />}
                {active === 'invest' && <PlacementsTab state={state} />}
                {active === 'market' && <MarketTab state={state} />}
                {active === 'notebook' && <NotebookTab state={state} />}
                {active === 'journal' && <JournalTab state={state} />}
              </section>
            </>
          )}
        </main>
        <Side state={state} />
      </div>
      <Modals state={state} />
      <Notices notices={state.notices} />
    </div>
  );
}
